import { ReplacementValue } from '@/common/graphql';

import type { ComboboxItemWithFormatted } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';

interface ReplacementValueSelectMultipleProps
  extends Omit<SelectMultipleProps<ComboboxItemWithFormatted<ReplacementValue>>, 'searchable' | 'value'> {
  value: Array<ReplacementValue> | null | undefined;
}

// FR-002's exact selectable-value wording (Low / Medium / High / Extreme) - intentionally not the
// longer descriptive `ReplacementValueLabels` used on the Off-boarding edit form's single-select
// (`ReplacementValueSelectChip`), which serves a different purpose.
const labels: Record<ReplacementValue, string> = {
  [ReplacementValue.Low]: 'Low',
  [ReplacementValue.Medium]: 'Medium',
  [ReplacementValue.High]: 'High',
  [ReplacementValue.Extreme]: 'Extreme',
};

const sortOrder = [ReplacementValue.Extreme, ReplacementValue.High, ReplacementValue.Medium, ReplacementValue.Low];

// Deliberately not `ObjectUtils.arrayFromEnumValues(ReplacementValue)` (the sibling
// EmployeeStatusSelect.tsx convention): importing `@/common/utils` from this file drags a
// pre-existing circular load into `@/common/types/filters/demographic-report-filter.ts` (which
// needs `DateUtils` back from `@/common/utils`) that only surfaces when a test importing this
// component is the module graph's entry point, requiring an increasingly fragile multi-member
// `vi.mock('@/common/utils', ...)` to work around. `sortOrder` is this enum's complete,
// intentionally-ordered value set (verified against `ReplacementValue`'s 4 members).
const data = sortOrder.map(value => ({ label: labels[value], value }));

export const ReplacementValueSelectMultiple = ({
  placeholder = 'Select replacement value',
  ...rest
}: ReplacementValueSelectMultipleProps) => (
  <SelectMultiple
    {...rest}
    data={data}
    loading={false}
    multipleSelectedLabel={length => `${length} values`}
    placeholder={placeholder}
    searchable={false}
  />
);
